import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import c from 'classnames';

import { Kit } from '@repo/types';
import { getAgeGroup, AgeGroup } from '@repo/utils';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useProfileSurveys from 'services/surveys/useProfileSurveys';
import { ProfileSurveys } from 'types/Surveys';

import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  LinkButton,
  Typography,
} from 'components';
import { CheckIcon } from './NextStep';
import { TypeformSurveyTime } from 'views/Results/Surveys/TypeformSurveyCard/TypeformSurveyCard';

import styles from './TypeformSurveysStep.module.css';

type TypeformSurveyRowProps = {
  survey: ProfileSurveys;
  needsSurveys: boolean;
};
const TypeformSurveyRow = ({
  survey,
  needsSurveys,
}: TypeformSurveyRowProps) => {
  const { pathname } = useLocation();
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  return (
    <div
      className={c(
        styles.surveyRow,
        survey.is_complete && styles.completed,
        !needsSurveys && styles.allCompleted,
      )}
    >
      <div className={styles.surveyRowTitle}>
        <Typography variant='heading-s'>{survey.name}</Typography>
        <CheckIcon isCompleted={survey.is_complete} />
      </div>
      {survey.is_complete ? (
        <div>
          <LinkButton
            label='See response'
            iconNameRight='chevronForward'
            href={`/results/${currentKitId}/surveys/${survey.typeform_survey_id}`}
            state={{ from: pathname }}
            size={ButtonSizes.S}
            color={ButtonColors.PURPLE}
            variant={ButtonVariants.TEXT}
          />
        </div>
      ) : (
        <div className='flex align-items-center justify-content-between gap-1'>
          <LinkButton
            label='Complete survey'
            iconNameRight='chevronForward'
            href={`/typeform/${currentTinyAccountId}/${currentKitId}/${survey.typeform_survey_id}`}
            state={{ from: pathname }}
            size={ButtonSizes.S}
            color={ButtonColors.DARK_PURPLE}
          />
          <TypeformSurveyTime time={survey.time_estimate} />
        </div>
      )}
    </div>
  );
};

/**
 * Resolves the short survey typeform ID for a kit based on its sampling type and age group.
 * Partner top-level columns take precedence over practitioner portal_config fields.
 */
export const getShortSurveyTypeformId = (kit: Kit): string | undefined => {
  if (!kit.sampling_type) return undefined;

  if (kit.sampling_type === 'vaginal') {
    return (
      kit.partner?.typeform_vaginal_survey_id ??
      kit.practitioner?.portal_config?.typeform_vaginal_survey_id
    );
  }

  if (
    kit.sampling_type === 'stool' &&
    [AgeGroup.CHILD, AgeGroup.ADULT].includes(
      getAgeGroup(kit.age_at_sampling ?? 0),
    )
  ) {
    return (
      kit.partner?.typeform_adult_survey_id ??
      kit.practitioner?.portal_config?.typeform_adult_survey_id
    );
  }

  return (
    kit.partner?.typeform_toddler_survey_id ??
    kit.practitioner?.portal_config?.typeform_toddler_survey_id
  );
};

export const TypeformSurveysStep = () => {
  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const { data: surveys, needsSurveys } = useProfileSurveys(
    currentTinyAccountId,
    currentKitId,
  );
  const { config: portalConfig } = usePortalConfigs(currentKit);
  const isShortSurvey = portalConfig.survey_type === 'short';

  const shortSurveyTypeformId = useMemo(() => {
    if (!isShortSurvey || !currentKit) return undefined;
    return getShortSurveyTypeformId(currentKit);
  }, [isShortSurvey, currentKit]);

  // For short survey mode, render a single synthetic survey entry
  if (isShortSurvey && shortSurveyTypeformId) {
    const shortSurvey: ProfileSurveys = {
      id: -1,
      name: 'Health Survey',
      typeform_survey_id: shortSurveyTypeformId,
      created_at: '',
      updated_at: '',
      time_estimate: null,
      is_active: true,
      is_complete: !!currentKit?.date_survey_completed,
      is_partner: false,
      form_properties: [],
    };

    return (
      <div>
        <div className={styles.description}>
          <Typography variant='body-s'>
            We have a few questions about your health and lifestyle.
          </Typography>
        </div>
        <TypeformSurveyRow
          survey={shortSurvey}
          needsSurveys={!shortSurvey.is_complete}
        />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.description}>
        <Typography variant='body-s'>
          We need a bit more info and won't be able to publish your report
          without it.
        </Typography>
      </div>

      {surveys
        ?.filter(survey => !survey.is_partner)
        .map(survey => (
          <TypeformSurveyRow
            key={survey.id}
            survey={survey}
            needsSurveys={needsSurveys}
          />
        ))}
    </div>
  );
};
